import type { ISubscriptionResponse } from '@escloud/types';
import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router';

import { subscriptionStatusFacts } from '@/lib/subscription';

/**
 * The visible half of the read-only subscription state (ESCLOUD-288): a
 * degraded tenant is deliberately NOT redirected — a clinician still reaches
 * existing cases and reports — so this banner is what says why nothing new can
 * be created. Presentation only: the refusal itself is server-side
 * (`403 subscription_read_only`), and `accessLevel` arrives server-derived on
 * the subscription response.
 */
export function ReadOnlyBanner({
    tenantId,
    subscription,
}: {
    tenantId: string;
    subscription: ISubscriptionResponse;
}) {
    // Which statuses are worth naming here is classified once, with every
    // other status question the client asks (`SUBSCRIPTION_STATUS_FACTS`).
    const reason = subscriptionStatusFacts(subscription.status).readOnlyNote;

    return (
        <div
            role="status"
            className="flex items-center gap-3 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm"
        >
            <AlertTriangle className="size-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
            <p className="min-w-0 flex-1">
                <span className="font-medium">Your workspace is read-only.</span>{' '}
                <span className="text-muted-foreground">
                    Existing cases, reports and downloads remain available, but nothing new can be created
                    until the subscription is restored
                    {reason ? ` (${reason})` : ''}.
                </span>
            </p>
            <Link
                to={`/settings/tenant/${tenantId}/billing`}
                className="shrink-0 font-medium underline underline-offset-2"
            >
                Manage billing
            </Link>
        </div>
    );
}
